/**
 * The action layer (#1798) — ONE declaration, FOUR derived artefacts.
 *
 * An action is "a surface of a model a user can be sent to". Declaring it once
 * yields, at build time:
 *
 *   1. the route array          (`layer.routes`)
 *   2. the sidebar menu items   (`layer.menuItems`)
 *   3. the list → form path     (`layer.detailPath(key, id)`)
 *   4. the Drawer/Page decision (`layer.detailSurfaceOf(key)`)
 *
 * Three properties of this module are load-bearing and must survive any edit:
 *
 * - **`permission` is required.** For a system of DERIVED routes an omitted
 *   permission is read by the next author as "inherits the main action" or "open
 *   to everyone", and NEITHER is defined anywhere. Omitting it is a compile
 *   error; actions that genuinely share a gate share an explicit constant.
 * - **`domain` is a function, never a static array.** A relative date ("today")
 *   evaluated at module load is frozen to the day the bundle booted.
 * - **The route description is NEUTRAL.** Core emits paths, kinds and payloads;
 *   turning those into `<Route>`/`createBrowserRouter` entries is the consuming
 *   project's adapter. Core never imports a router.
 *
 * Deliberately absent: an action/breadcrumb STACK (the router's own `location`
 * stack already is one) and `target: 'new'` (opening an action inside another
 * action's modal — the source of modal-in-modal this epic exists to delete).
 *
 * Frontend permissions are EXPERIENCE, not a gate: every key here must have a
 * backend 403 behind it. This module changes how permissions are declared, never
 * what they mean.
 */
import { failLoud } from './fail-loud';

/** Where the detail of a record is shown. Drives which routes exist at all. */
export const DETAIL_SURFACE = {
  /** A routed page only; a list row navigates away. */
  page: 'page',
  /** An in-place drawer only; no detail route is emitted. */
  drawer: 'drawer',
  /** Both — the drawer deep-links to the routed page. */
  both: 'both',
} as const;

export type DetailSurface = (typeof DETAIL_SURFACE)[keyof typeof DETAIL_SURFACE];

/** What a derived route renders. */
export const ROUTE_KIND = {
  list: 'list',
  detail: 'detail',
} as const;

export type RouteKind = (typeof ROUTE_KIND)[keyof typeof ROUTE_KIND];

/** The comparison an action's domain filter applies. Closed set, no free string. */
export const FILTER_OP = {
  eq: 'eq',
  ne: 'ne',
  in: 'in',
  gt: 'gt',
  gte: 'gte',
  lt: 'lt',
  lte: 'lte',
  contains: 'contains',
} as const;

export type FilterOp = (typeof FILTER_OP)[keyof typeof FILTER_OP];

export interface ActionDomainFilter {
  field: string;
  op: FilterOp;
  value: unknown;
}

/**
 * The fixed filter an action applies on top of its model.
 *
 * A FUNCTION, evaluated per read. As a static array, `value: today()` would be
 * computed once at module load and keep answering with the boot day forever.
 */
export type ActionDomain = () => readonly ActionDomainFilter[];

/**
 * The model an action group belongs to. Core needs only its identity — the
 * fields, API and rendering all belong to the consuming project.
 */
export interface ActionModel {
  name: string;
}

/**
 * The permission gates of one action. `list` and `read` are required because
 * every action derives at least a list route, and a detail surface that is not
 * `drawer`-only derives a detail route; the write gates are optional because an
 * action may legitimately be read-only.
 */
export interface ActionPermissions<TPermission extends string> {
  list: TPermission;
  read: TPermission;
  create?: TPermission;
  update?: TPermission;
  delete?: TPermission;
}

/** The sidebar placement of an action. Absent ⇒ reachable by URL, not by menu. */
export interface ActionMenu<TSection extends string, TIcon> {
  /** A REGISTERED section. `TSection` is bound by the host's `as const` map. */
  section: TSection;
  /** Ascending sort key WITHIN the section. */
  order: number;
  /** i18n key — never a translated string. */
  labelKey: string;
  icon: TIcon;
}

/** The views an action can route to. `detail` is absent for drawer-only actions. */
export interface ActionViews<TView> {
  list: TView;
  detail?: TView;
}

export interface ActionDecl<TSection extends string, TPermission extends string, TIcon, TView> {
  /** Path segment(s) WITHOUT a leading slash, e.g. `catalog/ingredients`. */
  path: string;
  /** Required. No inheritance, no default, no omission. */
  permission: ActionPermissions<TPermission>;
  detailSurface: DetailSurface;
  views: ActionViews<TView>;
  menu?: ActionMenu<TSection, TIcon>;
  domain?: ActionDomain;
  context?: Readonly<Record<string, string | number | boolean | null>>;
}

export type ActionMap<TSection extends string, TPermission extends string, TIcon, TView> = Record<
  string,
  ActionDecl<TSection, TPermission, TIcon, TView>
>;

/**
 * A router-neutral route description. The host adapter maps `kind` + `view` onto
 * its own router; nothing here knows what a `<Route>` is.
 */
export interface DerivedRoute<TPermission extends string, TView> {
  model: string;
  actionKey: string;
  kind: RouteKind;
  path: string;
  permission: TPermission;
  view: TView;
}

export interface DerivedMenuItem<TSection extends string, TPermission extends string, TIcon> {
  model: string;
  actionKey: string;
  section: TSection;
  order: number;
  labelKey: string;
  /** Absolute href, derived from the action path. */
  href: string;
  permission: TPermission;
  icon: TIcon;
}

export interface ActionLayer<
  TSection extends string,
  TPermission extends string,
  TIcon,
  TView,
  TActions extends ActionMap<TSection, TPermission, TIcon, TView>,
> {
  readonly model: ActionModel;
  readonly actions: TActions;
  readonly routes: readonly DerivedRoute<TPermission, TView>[];
  readonly menuItems: readonly DerivedMenuItem<TSection, TPermission, TIcon>[];
  /** The list → form navigation path for one record. */
  detailPath(action: keyof TActions & string, id: string | number): string;
  detailSurfaceOf(action: keyof TActions & string): DetailSurface;
  /** The action's fixed filter, evaluated NOW (never a module-load snapshot). */
  domainOf(action: keyof TActions & string): readonly ActionDomainFilter[];
}

function listPath(path: string): string {
  return path;
}

function detailPathTemplate(path: string): string {
  return `${path}/:id`;
}

/**
 * Bind the action layer to one project's section / permission / icon / view
 * types, then declare actions with the returned `defineActions`.
 *
 * Binding is what closes the `section` union: with `TSection` fixed to the
 * host's `as const` map, an unregistered section value fails to compile at the
 * declaration site rather than rendering into a section that does not exist.
 */
export function createActionLayer<
  TSection extends string,
  TPermission extends string,
  TIcon,
  TView,
>() {
  return function defineActions<TActions extends ActionMap<TSection, TPermission, TIcon, TView>>(
    model: ActionModel,
    actions: TActions,
  ): ActionLayer<TSection, TPermission, TIcon, TView, TActions> {
    const routes: DerivedRoute<TPermission, TView>[] = [];
    const menuItems: DerivedMenuItem<TSection, TPermission, TIcon>[] = [];
    const seen = new Set<string>();

    for (const [actionKey, decl] of Object.entries(actions)) {
      if (decl.path.startsWith('/')) {
        failLoud(
          `action ${model.name}.${actionKey}: path must not start with "/" (got "${decl.path}")`,
        );
      }

      const wantsDetailRoute = decl.detailSurface !== DETAIL_SURFACE.drawer;
      if (wantsDetailRoute && decl.views.detail === undefined) {
        failLoud(
          `action ${model.name}.${actionKey}: detailSurface "${decl.detailSurface}" needs views.detail`,
        );
      }

      const declared: DerivedRoute<TPermission, TView>[] = [
        {
          model: model.name,
          actionKey,
          kind: ROUTE_KIND.list,
          path: listPath(decl.path),
          permission: decl.permission.list,
          view: decl.views.list,
        },
      ];
      const detailView = decl.views.detail;
      if (wantsDetailRoute && detailView !== undefined) {
        declared.push({
          model: model.name,
          actionKey,
          kind: ROUTE_KIND.detail,
          path: detailPathTemplate(decl.path),
          permission: decl.permission.read,
          view: detailView,
        });
      }

      for (const route of declared) {
        if (seen.has(route.path)) {
          failLoud(`action ${model.name}.${actionKey}: duplicate route path "${route.path}"`);
          continue;
        }
        seen.add(route.path);
        routes.push(route);
      }

      const menu = decl.menu;
      if (menu !== undefined) {
        menuItems.push({
          model: model.name,
          actionKey,
          section: menu.section,
          order: menu.order,
          labelKey: menu.labelKey,
          href: `/${decl.path}`,
          permission: decl.permission.list,
          icon: menu.icon,
        });
      }
    }

    menuItems.sort((a, b) => a.order - b.order);

    // `keyof TActions & string` makes a miss impossible at the type level,
    // so there is no runtime existence check to write here.
    const declOf = (
      action: keyof TActions & string,
    ): ActionDecl<TSection, TPermission, TIcon, TView> => actions[action];

    return {
      model,
      actions,
      routes,
      menuItems,
      detailPath(action, id) {
        return `/${declOf(action).path}/${encodeURIComponent(String(id))}`;
      },
      detailSurfaceOf(action) {
        return declOf(action).detailSurface;
      },
      domainOf(action) {
        const domain = declOf(action).domain;
        return domain === undefined ? [] : domain();
      },
    };
  };
}
